import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api, { apiError, API } from "@/lib/api";
import Stars from "@/components/Stars";
import { TurnstileBox } from "@/components/TurnstileBox";

const ASPECTS = [
  ["cleanliness", "cleanliness"],
  ["sanitary", "sanitary"],
  ["welcome", "welcome_score"],
  ["value", "value"],
];

const fmt = (d) => (d ? new Date(d).toLocaleDateString("nl-BE", { day: "2-digit", month: "short", year: "numeric" }) : "");

function Shell({ camp, logo, children }) {
  return (
    <div className="min-h-screen bg-[#0b1623] text-white flex items-center justify-center px-5 py-12">
      <div className="w-full max-w-xl">
        <div className="flex items-center gap-3 mb-8">
          {logo && <img src={logo} alt="" className="h-11 w-auto rounded" />}
          <span className="text-lg font-semibold tracking-tight">{camp}</span>
        </div>
        <div className="rounded-2xl bg-white/[0.04] border border-white/10 backdrop-blur-sm p-7 sm:p-9">
          {children}
        </div>
        <p className="mt-6 text-center text-[11px] text-white/30">CampAd Pro</p>
      </div>
    </div>
  );
}

export default function Beoordeling() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [scores, setScores] = useState({});
  const [overall, setOverall] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [tsToken, setTsToken] = useState(null);

  useEffect(() => {
    api.get(`/public/review/${token}`)
      .then(({ data }) => setInfo(data))
      .catch((e) => setErr(apiError(e.response?.data?.detail) || e.message));
  }, [token]);

  if (err) {
    return (
      <Shell camp="CampAd Pro">
        <p className="text-white/70" data-testid="review-error">{err}</p>
      </Shell>
    );
  }
  if (!info) {
    return (
      <Shell camp="">
        <div className="flex items-center gap-3 text-white/60"><Loader2 className="h-5 w-5 animate-spin" />…</div>
      </Shell>
    );
  }

  const L = info.labels;
  const logo = info.logo_file_id ? `${API}/storage/files/${info.logo_file_id}` : null;

  if (done || info.submitted) {
    const title = done ? L.thanks_title : L.already_title;
    const body = done ? L.thanks_body : L.already_body;
    return (
      <Shell camp={info.camp_name} logo={logo}>
        <div className="text-center py-4" data-testid="review-thanks">
          <CheckCircle2 className="h-12 w-12 mx-auto text-[#5BBCE6]" />
          <h1 className="mt-5 text-2xl font-semibold">{title}</h1>
          <p className="mt-3 text-white/60">{body}</p>
        </div>
      </Shell>
    );
  }
  if (info.expired) {
    return (
      <Shell camp={info.camp_name} logo={logo}>
        <div className="text-center py-4" data-testid="review-expired">
          <Clock className="h-12 w-12 mx-auto text-amber-400" />
          <h1 className="mt-5 text-2xl font-semibold">{L.expired_title}</h1>
          <p className="mt-3 text-white/60">{L.expired_body}</p>
        </div>
      </Shell>
    );
  }

  const submit = async () => {
    if (!overall) { toast.error(L.required); return; }
    setBusy(true);
    try {
      await api.post(`/public/review/${token}`, { overall, ...scores, comment, turnstile: tsToken });
      setDone(true);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <Shell camp={info.camp_name} logo={logo}>
      <div data-testid="review-form">
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">{L.title}</h1>
        <p className="mt-3 text-white/60">{L.welcome.replace("{name}", info.first_name)}</p>

        <dl className="mt-6 flex flex-wrap gap-x-8 gap-y-2 text-sm border-y border-white/10 py-4">
          <div><dt className="text-white/40 text-xs uppercase tracking-wider">{L.pitch}</dt><dd className="font-mono">{info.pitch || "—"}</dd></div>
          <div><dt className="text-white/40 text-xs uppercase tracking-wider">{L.period}</dt><dd>{fmt(info.arrival)} → {fmt(info.departure)}</dd></div>
        </dl>

        <div className="mt-7 space-y-5">
          <div>
            <p className="font-medium">{L.overall}</p>
            <p className="text-xs text-white/40 mb-2">{L.stars_hint}</p>
            <Stars value={overall} onChange={setOverall} size="lg" testid="review-overall" />
          </div>
          {ASPECTS.map(([key, label]) => (
            <div key={key} className="flex items-center justify-between gap-4">
              <span className="text-sm text-white/70">{L[label]}</span>
              <Stars value={scores[key] || 0} onChange={(v) => setScores((s) => ({ ...s, [key]: v }))} testid={`review-${key}`} />
            </div>
          ))}
        </div>

        <div className="mt-7">
          <p className="font-medium mb-2">{L.comment}</p>
          <Textarea rows={4} value={comment} placeholder={L.comment_ph} maxLength={4000}
            onChange={(e) => setComment(e.target.value)} data-testid="review-comment"
            className="bg-white/5 border-white/15 text-white placeholder:text-white/30" />
        </div>

        <TurnstileBox siteKey={info.turnstile_site_key} onToken={setTsToken} />

        <Button onClick={submit} disabled={busy || (info.turnstile_site_key && !tsToken)} data-testid="review-submit"
          className="mt-7 w-full rounded-full bg-[#5BBCE6] text-[#06202c] hover:bg-[#7ccaed] font-semibold h-12">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : L.submit}
        </Button>
      </div>
    </Shell>
  );
}
